import { Button } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { useCallback, useContext } from "react";
import {useFormContext, useWatch} from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useRecoilValue } from "recoil";
import { AxiosError } from "axios";
import CustomLoader from "../../../../../../../util/component/CustomLoader";
import { setSnackbar } from "../../../../../../../util/state/globalAction";
import { GlobalContext } from "../../../../../../../util/state/GlobalContextProvider";
import { snackbarTypes } from "../../../../../../../util/state/globalVariables";
import { offer } from "../../../../../api/services";
import useDialog from "../../../../../hooks/useDialog";
import { LOCALE_DATE_FORMAT } from "../../../../../utils";
import { bookingIdState } from "../../../recoil";
import { IFormValues } from "../../../types";
import ConfirmDialog from "../confirm-dialog/ConfirmDialog";

const ButtonOffer = () => {

    const { t } = useTranslation('booking');

    const { dispatchGlobalData } = useContext(GlobalContext);

    const navigate = useNavigate();

    const bookingId = useRecoilValue(bookingIdState);

    const formContext = useFormContext<IFormValues>();

    const tenant = useWatch({ control: formContext.control, name: "basicDetails.tenants" });
    const dealer = useWatch({ control: formContext.control, name: "basicDetails.dealers" });

    const offerMutation = useMutation(
        offer,
        {
            onSuccess: (data) => {
                dispatchGlobalData({
                    type: setSnackbar,
                    payload: {
                        snackbar: {
                            message: (
                                t('message.info.new_booking') +
                                t(`status.${data.status}`)) +
                                (data.subStatus ? ` - ${t(`substatus.${data.subStatus}`)}` : '')
                            ,
                            type: snackbarTypes.info,
                            show: true,
                            autoClose: true,
                        }
                    }
                });
                navigate(`/booking/view/${data.id}`);
            },
            onError: (err) => {
                let message = '';
                if (err instanceof (AxiosError)) {
                    switch (err.response?.data.code) {
                        case '1':
                            message = t('message.error.main_driver_fiscal_code');
                            formContext.setError(
                                'firstDriver.fiscalCode',
                                {
                                    message: 'message.validation.invalid_fiscal_code'
                                }
                            )
                            break;
                        case '2':
                            message = t('message.error.billing_owner_fiscal_code');
                            formContext.setError(
                                'invoice.fiscalCode',
                                {
                                    message: 'message.validation.invalid_fiscal_code'
                                }
                            )
                            break;
                        case '3':
                            message = t('message.error.booking_owner_fiscal_code');
                            formContext.setError(
                                'bookingOwner.fiscalCode',
                                {
                                    message: 'message.validation.invalid_fiscal_code'
                                }
                            )
                            break;
                        case '50':
                            // ITDRIV-21208 — Limite "Fascia Noleggio" (rental_group) del BA superato
                            // al confirm dell'offer. Il BFF rimappa l'errore del MS su code "50",
                            // allineando il messaggio a quello mostrato dal pre-check allo step draft.
                            message = t('message.error.maximum_number_rentals');
                            break;
                        default:
                            message = t('message.error.default');
                    }
                }
                dispatchGlobalData({
                    type: setSnackbar,
                    payload: {
                        snackbar: {
                            message: message,
                            type: snackbarTypes.error,
                            show: true,
                            autoClose: true,
                        }
                    }
                });
            }
        }
    );

    const dialog = useDialog();

    const handleConfirm = useCallback(async () => {
        const data = formContext.getValues();
        if (
            !data.basicDetails.pickupLocation ||
            !data.basicDetails.dropoffLocation ||
            !data.basicDetails.carGroup ||
            !bookingId ||
            !data.firstDriver.mobilePrefix ||
            !data.bookingOwner.mobilePrefix
        ) {
            return;
        }
        await offerMutation.mutateAsync({
            id: bookingId,
            confirmData: {
                firstDriver: {
                    customerId: data.firstDriver.id,
                    name: data.firstDriver.name,
                    surname: data.firstDriver.surname,
                    birthDate: data.firstDriver.birthDate ? data.firstDriver.birthDate.format(LOCALE_DATE_FORMAT) : '',
                    birthNation: data.firstDriver.birthNation,
                    birthPlace: data.firstDriver.birthPlace,
                    residenceCountry: data.firstDriver.residenceCountry,
                    fiscalCode: data.firstDriver.fiscalCode,
                    drivingLicenseNumber: data.firstDriver.drivingLicenseNumber,
                    email: data.firstDriver.email,
                    mobile: data.firstDriver.mobile,
                    mobilePrefix: data.firstDriver.mobilePrefix,
                    gender: data.firstDriver.gender
                },
                bookingOwner: {
                    customerId: data.bookingOwner.id,
                    name: data.bookingOwner.name,
                    surname: data.firstDriver.surname,
                    birthDate: data.bookingOwner.birthDate ? data.bookingOwner.birthDate.format(LOCALE_DATE_FORMAT) : '',
                    birthNation: data.bookingOwner.birthNation,
                    birthPlace: data.bookingOwner.birthPlace,
                    residenceCountry: data.bookingOwner.residenceCountry,
                    fiscalCode: data.bookingOwner.fiscalCode,
                    drivingLicenseNumber: data.bookingOwner.drivingLicenseNumber,
                    email: data.bookingOwner.email,
                    mobile: data.bookingOwner.mobile,
                    mobilePrefix: data.bookingOwner.mobilePrefix
                },
                bookingCompanyOwner: {
                    customerId: data.bookingOwner.id,
                    companyName: data.bookingOwner.companyName,
                    country: data.bookingOwner.country,
                    city: data.bookingOwner.city,
                    streetName: data.bookingOwner.streetName,
                    streetNumber: data.bookingOwner.streetNumber,
                    postalCode: data.bookingOwner.postalCode,
                    email: data.bookingOwner.email,
                    pec: data.bookingOwner.certifiedEmail,
                    telephone: data.bookingOwner.telephone,
                    mobile: data.bookingOwner.mobile,
                    mobilePrefix: data.bookingOwner.mobilePrefix,
                    vatNumber: data.bookingOwner.vatNumber,
                    vatNumberCountry: data.bookingOwner.vatNumberCountry,
                    fiscalCode: data.bookingOwner.fiscalCode,
                    customerCode: data.basicDetails.customerCode ?? '',
                    sdiCode: data.bookingOwner.invoiceCode,
                    sapId: '',
                    legalEntity: data.bookingOwner.legalEntity,
                    publicAuthority: data.bookingOwner.publicAuthority ? '1' : '0',
                    paIdNumber: data.bookingOwner.publicAuthority ? data.bookingOwner.paIdNumber : '',
                    siren: data.bookingOwner.siren,
                    siret: data.bookingOwner.siret,
                },
                invoice: {
                    customerId: data.invoice.id,
                    type: data.invoice.type,
                    name: data.invoice.name,
                    surname: data.invoice.surname,
                    birthDate: data.invoice.birthDate ? data.invoice.birthDate.format(LOCALE_DATE_FORMAT) : '',
                    birthNation: data.invoice.birthNation,
                    birthPlace: data.invoice.birthPlace,
                    residenceCountry: data.invoice.residenceCountry,
                    fiscalCode: data.invoice.fiscalCode,
                    email: data.invoice.email,
                    mobile: data.invoice.mobile,
                    drivingLicenseNumber: data.invoice.drivingLicenseNumber,
                    billingCountry: data.invoice.billingCountry,
                    billingCity: data.invoice.billingCity,
                    billingStreetName: data.invoice.billingStreetName,
                    billingStreetNumber: data.invoice.billingStreetNumber,
                    billingPostalCode: data.invoice.billingPostalCode,
                    pec: data.invoice.certifiedEmail,
                    vatNumber: data.invoice.vatNumber,
                    vatNumberCountry: data.invoice.vatNumberCountry,
                    invoiceCode: data.invoice.invoiceCode,
                    companyName: data.invoice.companyName,
                    mobilePrefix: data.invoice.mobilePrefix,
                    billingProvince: data.invoice.billingProvince,
                    legalEntity: data.invoice.legalEntity,
                    publicAuthority: data.invoice.publicAuthority ? '1' : '0',
                    paIdNumber: data.invoice.publicAuthority ? data.invoice.paIdNumber : '',
                    siren: data.invoice.siren,
                    siret: data.invoice.siret,
                },
                additionalDrivers: data.additionalDrivers.map(ad => ({
                    customerId: ad.id,
                    name: ad.name,
                    surname: ad.surname,
                    email: ad.email
                })),
            },
            tenant: tenant,
            dealer: dealer
        });
    }, []);

    const handleClick = useCallback(() => {
        dialog.handleShow();
    }, []);

    return (
        <>
            <Button onClick={formContext.handleSubmit(handleClick)}>
                {t('label.confirm_offer')}
            </Button>
            <ConfirmDialog
                title={t('label.confirm_offer')}
                open={dialog.open}
                onClose={dialog.handleClose}
                onConfirm={handleConfirm}
                isOffer={true}
            />
            <CustomLoader loading={offerMutation.isLoading} />
        </>
    );
};

export default ButtonOffer;
